import { path, prop, contains, inc, splitEvery } from 'ramda';
import { getTripLocalTime } from '../react/selectors/trips';
import { getPriceAsNumber, getPriceStr } from './utils';
import DateHelper from './date-helper';
const OWOX_PRODUCT_LIMIT = 10;
const RATING_DIMENSION_MAPPING = {
  avg_rating: 'dimension16',
  travel_time: 'dimension17',
  in_advance: 'dimension18',
  distance: 'dimension27',
  cleanless: 'dimension28',
  politeness: 'dimension29',
  punctuality: 'dimension30',
  technical_condition: 'dimension31',
  votes_count: 'dimension32',
  busfor_recommends: 'dimension33',
  air_conditioning: 'dimension34',
  seat_comfort: 'dimension35',
  'wi-fi': 'dimension36',
  discount: 'metric2',
  returnDate: 'dimension37'
};
function tripRating(trip) {
  const {
    rating
  } = trip;
  if (rating) {
    return {
      technical_condition: rating['technical-condition'],
      punctuality: rating.punctuality,
      politeness: rating.politeness,
      cleanless: rating.cleanless,
      avg_rating: rating['avg-rating'],
      votes_count: rating['votes-count']
    };
  }
  return null;
}
function transformRatingToDimensions(rating) {
  if (rating) {
    const ratingDimensions = {};
    Object.keys(RATING_DIMENSION_MAPPING).forEach(ratingKey => {
      const dimensionKey = RATING_DIMENSION_MAPPING[ratingKey];
      if (dimensionKey) {
        ratingDimensions[dimensionKey] = rating[ratingKey];
      }
    });
    return ratingDimensions;
  }
  return null;
}
const createRoundTime = time => {
  let [hours, minutes] = time.split(':');
  hours |= hours;
  minutes |= minutes;
  if (minutes >= 30) {
    hours = inc(hours);
  }
  minutes = '00';
  return `${hours}:${minutes}`;
};
const preparedOWOXproducts = ({
  products,
  directionId = '',
  distance = null
}) => products.map(product => {
  const tripLocalTime = getTripLocalTime({
    date: product['start-date'],
    time: product['start-time'],
    zone: product['start-datetime-zone']
  });
  return {
    id: product.id,
    name: product.number,
    category: directionId ? `${product.internationality}/${directionId}` : null,
    brand: product['carrier-name'],
    variant: `${product['start-date']}, ${product['start-time']}`,
    price: getPriceAsNumber(product.price).toString(),
    metric1: getPriceAsNumber(product.price),
    position: product.idx,
    ...transformRatingToDimensions(tripRating(product)),
    metric2: (product['discount-percent'] || null) && parseInt(product['discount-percent'], 10),
    [RATING_DIMENSION_MAPPING.busfor_recommends]: product['busfor-recommends'] ? '1' : '0',
    [RATING_DIMENSION_MAPPING.in_advance]: DateHelper.day.diff(new Date(tripLocalTime), DateHelper.day.today),
    [RATING_DIMENSION_MAPPING['wi-fi']]: product.options.includes('wi-fi') ? '1' : '0',
    [RATING_DIMENSION_MAPPING.seat_comfort]: product.options.includes('comfort-seats') ? '1' : '0',
    [RATING_DIMENSION_MAPPING.air_conditioning]: product.options.includes('climate-control') ? '1' : '0',
    [RATING_DIMENSION_MAPPING.distance]: distance && Math.floor(distance),
    [RATING_DIMENSION_MAPPING.travel_time]: parseInt(product['time-in-road'], 10) ? parseInt(createRoundTime(product['time-in-road']), 10) : null
  };
});
const preparedOWOXproductsSschedule = ({
  products,
  directionId = ''
}) => products.map(({
  id,
  idx,
  price,
  number: name,
  internationality = '',
  frequency,
  ...product
}) => ({
  id,
  name,
  category: directionId ? `${internationality}/${directionId}` : null,
  variant: `${frequency}, ${product['start-time']}`,
  price: getPriceStr(price),
  metric1: getPriceAsNumber(price),
  position: inc(idx),
  brand: product['carrier-name'],
  list: 'timetable'
}));
export const pushToDataLayer = event => {
  if (!window.dataLayer) window.dataLayer = [];
  window.dataLayer.push(event);
};
export const createSearchProduct = ({
  trip,
  passengersCount: quantity,
  position,
  pageCategory: list,
  direction,
  isInternational = false,
  tripsWithLowestPrices
}) => ({
  name: `${trip['start-city-name']} - ${trip['end-city-name']}`,
  variant: `${trip['start-date']} ${trip['start-time']}`,
  price: getPriceAsNumber(trip.price) / quantity,
  brand: trip['carrier-name'],
  position,
  list,
  quantity,
  direction,
  category: isInternational ? 'international' : 'internal',
  rating: tripRating(trip),
  bargain: contains(trip, tripsWithLowestPrices) ? 1 : 0
});
export const createOWOXProductList = ({
  products,
  type,
  directionId,
  distance,
  cityNameFrom,
  cityNameTo,
  departureDate,
  adultFare,
  childFare,
  pageViewStatus
}) => {
  const productsQuantityTotal = products.length;
  if (!productsQuantityTotal) return null;
  const productsWithIdx = products.map((product, idx) => ({
    ...product,
    idx: inc(idx)
  }));
  const splitedOWOXproducts = splitEvery(OWOX_PRODUCT_LIMIT, preparedOWOXproducts({
    products: productsWithIdx,
    directionId,
    distance
  }));
  return splitedOWOXproducts.map((chunk, idx) => pushToDataLayer({
    event: pageViewStatus && idx === 0 ? 'OWOX_pageview' : 'OWOX',
    eventCategory: 'Non-Interactions',
    eventAction: 'show',
    eventLabel: 'search_result',
    pageType: 'productPage',
    roundTrip: '0',
    cityNameFrom,
    cityNameTo,
    departureDate,
    adultFare,
    childFare,
    productsQuantityTotal,
    OWOX_ecommerce: {
      currencyCode: window.gon.currencyCode,
      detail: {
        actionField: {
          list: type
        },
        products: chunk
      }
    }
  }));
};
export const createOWOXProductListSchedule = ({
  products,
  type,
  directionId
}) => {
  const splitedOWOXproducts = splitEvery(OWOX_PRODUCT_LIMIT, preparedOWOXproductsSschedule({
    products,
    directionId
  }));
  return splitedOWOXproducts.map(chunk => pushToDataLayer({
    pageType: type,
    OWOX_ecommerce: {
      currencyCode: window.gon.currencyCode,
      impressions: chunk
    }
  }));
};
export const createSearchEvent = ({
  currencyCode,
  isCompleted = false,
  products: impressions
}) => ({
  event: isCompleted ? 'searchCompleted' : 'search',
  searchType: impressions && impressions.length > 0 ? 'result' : 'zero',
  ecommerce: {
    currencyCode,
    impressions,
    image: path(['gon', 'destinationImageUrl'], global) || '',
    price_m: impressions.map(prop('price'))
  }
});
export const createProductClick = ({
  currencyCode,
  pageCategory: list,
  products
}) => ({
  event: 'productClick',
  ecommerce: {
    currencyCode,
    detail: {
      actionField: {
        list
      },
      products
    }
  }
});
const checkoutStepEvent = ({
  paymentOptionName: option,
  step,
  products
}) => ({
  event: 'checkout',
  roundTrip: '0',
  ecommerce: {
    checkout: {
      actionField: {
        step,
        option
      },
      products
    }
  }
});
export const createCheckoutStep2Event = ({
  paymentOptionName,
  products
}) => checkoutStepEvent({
  step: 2,
  paymentOptionName,
  products
});
export const createSuccessfulSubscriptionEvent = emailHash => {
  pushToDataLayer({
    event: 'OWOX',
    eventCategory: 'Conversions',
    eventAction: 'click',
    eventLabel: 'subscribe',
    eventPrivate1: emailHash
  });
  pushToDataLayer({
    event: 'subscribe',
    privat1: emailHash
  });
};
export const createSeatSelectingIsPossibleEvent = () => pushToDataLayer({
  event: 'seatselect'
});
export const createSeatMapOpenEvent = () => pushToDataLayer({
  event: 'chooseseat'
});
export const createSelectSeatEvent = () => pushToDataLayer({
  event: 'seatchoosed'
});
export const createTripDetailsConditionOpenEvent = () => pushToDataLayer({
  event: 'trip_details_condition_open'
});
export const createTripDetailsConditionCloseEvent = () => pushToDataLayer({
  event: 'trip_details_condition_close'
});
const createPassengerBody = ({
  id,
  price: {
    // eslint-disable-next-line no-unused-vars
    value: priceValue
  },
  arrayPosition,
  category,
  'start-city-name': from,
  'end-city-name': to,
  'carrier-name': brandName,
  'start-date': startDate,
  'start-time': startTime,
  'discount-percent': discount = 0
}, price, quantity) => {
  const tripName = `${from} - ${to}`;
  const tripStartDate = `${startDate} ${startTime}`;
  return {
    id,
    name: tripName,
    category,
    brand: brandName,
    position: arrayPosition,
    variant: tripStartDate,
    [RATING_DIMENSION_MAPPING.discount]: discount,
    price: String(price),
    metric1: price,
    quantity
  };
};
const getPassengerData = ({
  'adult-fare': adultFare,
  'child-fare': childFare,
  internationality,
  ...tripData
}, {
  adults,
  children
}, directionId) => {
  const {
    value: childValue = '0'
  } = childFare || {};
  const {
    value: adultValue = '0'
  } = adultFare || {};
  const userBody = [];
  const category = `${internationality}/${directionId}`;
  const trip = {
    ...tripData,
    category
  };
  userBody.push(createPassengerBody(trip, adultValue, adults.count));
  if (children.length > 0) {
    userBody.push(createPassengerBody(trip, childValue, children.length));
  }
  return userBody;
};
export const createAddGoodsToBasketEvent = (trip, directionId = '0', list = 'offer', passengers, returnDate) => {
  const {
    price: {
      currency: currencyCode
    }
  } = trip;
  pushToDataLayer({
    event: 'OWOX',
    eventCategory: 'Conversions',
    eventAction: 'add',
    eventLabel: 'cart',
    roundTrip: '0',
    ...(returnDate && {
      [RATING_DIMENSION_MAPPING.returnDate]: returnDate
    }),
    OWOX_ecommerce: {
      currencyCode,
      add: {
        actionField: {
          list
        },
        products: getPassengerData(trip, passengers, directionId)
      }
    }
  });
};
export const createClickOnTripDetailsInfoEvent = (tripId, tripInfoStatus) => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Conversions',
  eventAction: 'click',
  eventLabel: 'details',
  eventContext: tripId,
  eventContent: tripInfoStatus,
  OWOX_ecommerce: null,
  roundTrip: '0'
});
export const createSortEvent = (eventContext, eventContent) => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Interactions',
  eventAction: 'click',
  eventLabel: 'sorted',
  OWOX_ecommerce: null,
  roundTrip: '0',
  eventContent,
  eventContext
});
export const createSuccessfulSaveEvent = (hashedPhone, hashedEmail) => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Interactions',
  eventAction: 'click',
  eventLabel: 'changeContacts',
  eventPrivate1: hashedEmail,
  eventPrivate2: hashedPhone
});
export const createShowInsuranceEvent = ({
  insurancePrice,
  insuranceName
}) => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Non-Interactions',
  eventAction: 'show',
  eventLabel: 'insurance',
  eventContext: insurancePrice,
  eventContent: insuranceName,
  roundTrip: '0'
});
export const createInsuranceEvent = ({
  option,
  insurancePrice,
  insuranceName = ''
}) => {
  const optionName = `${option} ${insuranceName}`;
  pushToDataLayer({
    event: 'OWOX',
    eventCategory: 'Conversions',
    eventAction: 'click',
    eventLabel: 'insurance',
    eventContext: insurancePrice,
    eventContent: optionName,
    roundTrip: '0',
    OWOX_ecommerce: {
      checkout_option: {
        actionField: {
          step: 2,
          option: optionName
        }
      }
    }
  });
  const oldTypes = {
    'with insurance': 'Insurance_yes',
    'without insurance': 'Insurance_no'
  };
  pushToDataLayer({
    event: oldTypes[option],
    stage: 'Preloader',
    name: 'Insurance'
  });
};
export const createSearchFormEvent = ({
  type,
  content
}) => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Interactions',
  eventAction: 'switch',
  eventLabel: type,
  eventContent: content,
  OWOX_ecommerce: null
});
export const createCheckoutSubmitEvent = ({
  hashedEmail,
  hashedPhone
}) => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Conversions',
  eventAction: 'click',
  eventLabel: 'pay',
  eventPrivate1: hashedEmail,
  eventPrivate2: hashedPhone,
  roundTrip: '0'
});
const getPreperedSeats = (seats, segmentsCount) => {
  const seatsArr = [];
  for (let i = 0; i < segmentsCount; i++) {
    if (seats[i] && seats[i].length > 0) {
      seatsArr.push(seats[i].join(','));
    } else {
      seatsArr.push('null');
    }
  }
  return seatsArr.join('|');
};
export const createCheckoutSeatmapEvent = ({
  seats,
  segmentsCount
}) => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Conversions',
  eventAction: 'click',
  eventLabel: 'choose_seats',
  eventContent: getPreperedSeats(seats, segmentsCount),
  roundTrip: '0',
  OWOX_ecommerce: {
    checkout_option: {
      actionField: {
        step: 1,
        option: getPreperedSeats(seats, segmentsCount)
      }
    }
  }
});
export const createPayEvent = ({
  method
}) => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Conversions',
  eventAction: 'switch',
  eventLabel: 'paymentMethod',
  eventContent: method,
  roundTrip: '0',
  OWOX_ecommerce: {
    checkout_option: {
      actionField: {
        step: 3,
        option: method
      }
    }
  }
});
export const createTripEvent = (tripName, minPrice = null) => {
  const dataContent = {
    event: 'OWOX',
    eventCategory: 'Interactions',
    eventAction: 'click',
    eventLabel: 'product',
    eventContent: tripName,
    OWOX_ecommerce: null
  };
  if (minPrice) {
    dataContent.eventContext = minPrice;
  }
  pushToDataLayer(dataContent);
};
export const createSuccessfulAuthEvent = ({
  hashedPhone,
  page
}) => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Interactions',
  eventAction: 'click',
  eventLabel: 'signIn',
  eventPrivate2: hashedPhone,
  eventContext: page
});
export const createNearCitiesOrDatesEvent = ({
  type,
  content
}) => {
  const types = {
    nearDate: 'departureDate',
    nearCity: 'cityNameTo'
  };
  pushToDataLayer({
    event: 'OWOX',
    eventCategory: 'Non-Interactions',
    eventAction: 'show',
    eventLabel: type,
    pageType: 'nobus',
    [types[type]]: content.join(', ')
  });
};
export const createNobusEvent = ({
  cityNameFrom,
  cityNameTo,
  departureDate,
  adultFare,
  childFare
}) => pushToDataLayer({
  event: 'OWOX_pageview',
  eventCategory: 'Non-Interactions',
  eventAction: 'show',
  eventLabel: 'search_result_null',
  pageType: 'nobus',
  roundTrip: '0',
  productsQuantityTotal: 0,
  cityNameFrom,
  cityNameTo,
  departureDate,
  adultFare,
  childFare,
  OWOX_ecommerce: {
    currencyCode: global.gon.currencyCode,
    detail: {}
  }
});
export const createFindFormEvent = () => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Conversions',
  eventAction: 'click',
  eventLabel: 'findTicket',
  roundTrip: '0'
});
export const createTimetableTripEvent = ({
  trips
}) => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Interactions',
  eventAction: 'click',
  eventLabel: 'tab',
  pageType: 'schedule_catalog',
  OWOX_ecommerce: {
    currencyCode: global.gon.currencyCode,
    impressions: trips
  }
});
export const createPromoBannerEvent = ({
  id,
  eventName
}) => pushToDataLayer({
  event: eventName,
  name: id
});
export const createClickOnGoToDirectTripsEvent = () => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Conversions',
  eventAction: 'click',
  eventLabel: 'roundtrip',
  eventContent: 'find',
  OWOX_ecommerce: null
});
export const createContinueRtEvent = () => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Conversions',
  eventAction: 'click',
  eventLabel: 'roundtrip',
  eventContent: 'checkout',
  OWOX_ecommerce: null
});
export const createTabDetailsClickEvent = (tabName, tripId) => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Conversions',
  eventAction: 'click',
  eventLabel: 'tripDetails',
  eventContext: tripId,
  eventContent: tabName,
  OWOX_ecommerce: null,
  roundTrip: '0'
});
export const createPartnerBannerShowEvent = eventContext => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Non-Interactions',
  eventAction: 'show',
  eventLabel: 'typ_banner',
  eventContext
});
export const createPartnerBannerClickEvent = eventContext => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Interactions',
  eventAction: 'click',
  eventLabel: 'typ_banner',
  eventContext
});
export const createCheckoutAuthModalEvent = () => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Non-Interactions',
  eventAction: 'show',
  eventLabel: 'authorization'
});
export const createTypShowEvent = eventLabel => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Non-Interactions',
  eventAction: 'show',
  eventContent: 'typ',
  eventLabel
});
export const createTypClickEvent = (eventLabel, eventContext = '') => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Interactions',
  eventAction: 'click',
  eventContent: 'typ',
  eventLabel,
  eventContext
});
export const createOpenPassengerAutoCompleteEvent = () => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Non-Interactions',
  eventAction: 'show',
  eventLabel: 'saved_passengers'
});
export const createSelectPassengerEvent = () => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Interactions',
  eventAction: 'click',
  eventLabel: 'saved_passengers_add'
});
export const createDeletePassengerEvent = () => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Interactions',
  eventAction: 'click',
  eventLabel: 'saved_passengers_delete'
});
export const createRestorePassengerEvent = () => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Interactions',
  eventAction: 'click',
  eventLabel: 'saved_passengers_restore'
});
export const createPaymentEvent = paymentOption => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Conversions',
  eventAction: 'switch',
  eventLabel: 'paymentMethod',
  eventContent: paymentOption
});
export const createSameTicketClickEvent = () => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Interactions',
  eventAction: 'click',
  eventLabel: 'sameTicket'
});
export const createSameTicketShowChildPickerEvent = () => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Non-Interactions',
  eventAction: 'show',
  eventLabel: 'childpicker'
});
export const createSameTicketChildPickerSubmitEvent = () => pushToDataLayer({
  event: 'OWOX',
  eventCategory: 'Interactions',
  eventAction: 'click',
  eventLabel: 'sameTicket_with_child'
});